"use client"

import { AnimatedElement } from "@/components/animations"
import { Phase } from "@/lib/phases"
import { cn } from "@/lib/utils"
import Link from "next/link"
import { motion } from "framer-motion"
import PhaseIcon from "./PhaseIcon"

const MotionDiv = motion.create("div")

type JourneyLineProps = {
  phases: Phase[]
  className?: string
}

export function JourneyLine({ phases, className = "" }: JourneyLineProps) {
  return (
    <div className={cn("my-12 flex flex-col items-center", className)}>
      <div className="w-full">
        <div className="relative">
          {/* SVG connection lines rendered first */}
          <div className="absolute inset-0 hidden md:block">
            <div className="flex h-24 items-center justify-between">
              {phases.map((_, index) => {
                if (index === phases.length - 1) return null
                return (
                  <div
                    key={`connector-${index}`}
                    className="h-[2px] flex-1 bg-gray-300 dark:bg-gray-700"
                  />
                )
              })}
            </div>
          </div>

          {/* Circles rendered on top, in a separate layer */}
          <div className="relative flex flex-col items-center justify-center space-y-12 md:flex-row md:justify-between md:space-y-0">
            {phases.map((phase, index) => (
              <Link
                key={phase.id}
                href={`#phase-${phase.id}`}
                className="group flex flex-col items-center"
              >
                {/* Phase circle with icon */}
                <MotionDiv
                  className={`flex h-24 w-24 items-center justify-center rounded-full text-white ${phase.colorClass.bgColor} shadow-md transition-all duration-300 group-hover:scale-105 group-hover:shadow-lg`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.2 }}
                  whileHover={{
                    scale: 1.08,
                    boxShadow:
                      "0 8px 30px color-mix(in oklch, var(--color-black), transparent 88%)",
                  }}
                >
                  <PhaseIcon
                    type={phase.id as "plan" | "build" | "run"}
                    size="lg"
                  />
                </MotionDiv>

                {/* Phase label */}
                <MotionDiv
                  className="mt-4 text-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.2 + 0.1 }}
                >
                  <span
                    className={`font-bold text-xl ${phase.colorClass.text} transition-all duration-300 group-hover:text-primary`}
                  >
                    {phase.title}
                  </span>
                </MotionDiv>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Journey description */}
      <MotionDiv
        className="mt-10 text-center text-base text-gray-700 dark:text-gray-300"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <p className="font-medium">
          A proven journey from strategy through execution to operation
        </p>
      </MotionDiv>
    </div>
  )
}

export const JourneyLineGradient = ({
  className = "",
}: {
  className?: string
}) => {
  return (
    <AnimatedElement
      className={`h-0.5 w-full bg-gradient-to-r from-primary/20 via-primary to-primary/20 ${className}`}
      delay={0.2}
    >
      <div />
    </AnimatedElement>
  )
}
